import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Controller, FieldErrors, UseFormTrigger } from 'react-hook-form';
import { getColor } from '../../../assets/theme';
import { memo } from 'react';
import { dateFormat } from '../../../utils/format';
import { Box, SxProps } from '@mui/material';
import DOMPurify from 'dompurify';
import { Moment } from 'moment';

export const reversDatePickerStyle: SxProps = {
  '& .MuiInputBase-root': {
    flexDirection: 'row-reverse',
    p: 0,
  },
  '& .MuiInputAdornment-root': {
    pr: 0.5,
  },
  '& .MuiInput-underline input': { pl: 1.7 },
  '& .MuiInputAdornment-standard': {
    ml: '-5px',
  },
  '& .MuiInputAdornment-root .MuiButtonBase-root': {
    color: '#00000089',
  },
};

type PickersActionBarAction = 'cancel' | 'accept' | 'today' | 'clear';

/**
 * Interface for DatePickerController props
 * @template TDate The type of the date value
 */
interface DatePickerInterface<TDate> extends Omit<DatePickerProps<TDate>, ''> {
  control: any;
  name: string;
  label: string;
  style?: React.CSSProperties;
  addOnChange?: (params: Moment | null) => void;
  disablePast?: boolean;
  errors?: FieldErrors<any>;
  trigger?: UseFormTrigger<any>;
  wrapperStyle?: SxProps;
  actions?: PickersActionBarAction[];
  sx?: SxProps;
}

/**
 * DatePickerController component
 * A customized date picker component that integrates with react-hook-form
 *
 * @template TDate The type of the date value
 * @param {DatePickerInterface<TDate>} props The component props
 * @returns {JSX.Element} The rendered DatePickerController
 */
const DatePickerController = <TDate,>({
  control,
  name,
  label,
  style = {},
  addOnChange,
  disablePast = true,
  errors,
  trigger,
  wrapperStyle,
  actions,
  ...etc
}: DatePickerInterface<TDate>) => {
  const { textColor } = getColor();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        return (
          <Box
            sx={{
              width: '100%',
              '& .MuiFormControl-root': {
                width: '100%',
              },
              ...wrapperStyle,
            }}
          >
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                slots={{
                  textField: () => (
                    <DatePicker
                      {...field}
                      onChange={(date) => {
                        field.onChange(date);
                        addOnChange && addOnChange(date as Moment);
                      }}
                      disablePast={disablePast}
                      format={dateFormat}
                      label={
                        label ? (
                          <span
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(label),
                            }}
                          />
                        ) : undefined
                      }
                      slotProps={{
                        field: {
                          readOnly: true,
                        },
                        textField: {
                          size: 'small',
                          variant: etc?.readOnly ? 'standard' : 'outlined',
                          InputLabelProps: { shrink: true },
                          placeholder: dateFormat,
                          sx: {
                            ...etc?.sx,
                            '& .MuiInputAdornment-root': {
                              ml: etc?.readOnly ? '0!important' : 'default',
                            },
                          },
                        },
                        ...(actions && { actionBar: { actions } }),
                        layout: {
                          sx: {
                            '& .MuiPickersCalendarHeader-switchViewButton, .MuiPickersArrowSwitcher-root button':
                              {
                                border: 0,
                                color: '#161C248F',
                                bgcolor: 'transparent',
                              },
                            '.MuiDialogActions-root button': {
                              width: '100%',
                              bgcolor: '#DFE3E8',
                              color: textColor,
                            },
                          },
                        },
                      }}
                      {...etc}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& > fieldset': {
                            borderColor: errors && errors[name] && 'error.main',
                          },
                        },
                        '& .MuiInputAdornment-root button': {
                          border: 0,
                          color: '#161C248F',
                          bgcolor: 'transparent',
                        },
                        ...style,
                        ...etc?.sx,
                      }}
                      onClose={() => {
                        etc?.onClose?.();
                        if (trigger && errors?.[name]) {
                          trigger(name);
                        }
                      }}
                    />
                  ),
                }}
              />
            </LocalizationProvider>
          </Box>
        );
      }}
    />
  );
};

export default memo(DatePickerController);
